/**
 * Mensagens do WhatsApp como no app: balões à esquerda (os outros) e à
 * direita (a equipe), separador de dia, nome do autor a cada troca de autor
 * (em grupo, com o número na dica), hora dentro do balão. É o ÚNICO balão do
 * módulo: a tela de Conversas, a ficha do cliente e as mensagens de origem
 * usam este.
 *
 * Link é texto: nada é clicável dentro do balão. Ao lado dele, para quem
 * responde, a ação "Responder" (citando a mensagem). Mensagem para a equipe
 * de projetos leva a marca "Projetos" junto da hora: com cor enquanto o
 * alerta está aberto, neutra depois.
 */
import React, { useLayoutEffect, useMemo, useRef } from "react";
import {
  FiBarChart2,
  FiCamera,
  FiCornerUpLeft,
  FiFileText,
  FiImage,
  FiMapPin,
  FiMic,
  FiPenTool,
  FiSlash,
  FiSmile,
  FiUser,
  FiVideo,
} from "react-icons/fi";
import styled, { css } from "styled-components";

import {
  agruparMensagens,
  autorDaMensagem,
  dicaDaApagada,
  horaCurta,
  marcaDeProjetos,
  midiaDaMensagem,
  trechoDaMensagem,
} from "./apresentacao";
import ImagemDaMensagem from "./ImagemDaMensagem";

/** O ícone do alerta Projetos, o mesmo na lista, no filtro, na faixa e na mensagem. */
export const IconeProjetos = FiPenTool;

export const ICONE_DA_MIDIA = Object.freeze({
  imagem: FiCamera,
  audio: FiMic,
  video: FiVideo,
  documento: FiFileText,
  figurinha: FiSmile,
  localizacao: FiMapPin,
  contato: FiUser,
  enquete: FiBarChart2,
});

/**
 * Fundo da conversa. As cores do balão ficam em variáveis para o tema
 * escuro trocar num lugar só (no escuro o fundo afunda e o balão sobe).
 */
export const FundoDaConversa = styled.div`
  --cp-fundo: var(--surface-2);
  --cp-balao: var(--surface);
  --cp-balao-meu: var(--accent-soft);
  --cp-midia: rgba(11, 15, 26, 0.05);

  [data-theme="dark"] & {
    --cp-fundo: var(--bg);
    --cp-balao: var(--surface-2);
    --cp-midia: rgba(255, 255, 255, 0.06);
  }

  background: var(--cp-fundo);
  min-width: 0;
`;

/** Caixa com o fundo da conversa para mostrar mensagens fora da tela de Conversas (ficha, origem). */
const CaixaDeMensagens = styled(FundoDaConversa)`
  width: 100%;
  max-height: 480px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 6px 16px 2px;
  border-radius: 12px;
  border: 1px solid var(--border);
`;

const Lista = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

/* Um dia por grupo, com o rótulo centrado no começo. */
const Dia = styled.li`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const RotuloDoDia = styled.div`
  align-self: center;
  margin: 12px 0 10px;
  padding: 5px 12px;
  border-radius: 8px;
  background: var(--cp-balao);
  box-shadow: var(--shadow-1);
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-2);

  li:first-child > & {
    margin-top: 4px;
  }
`;

export const LinhaDoBalao = styled.li`
  display: flex;
  flex-direction: column;
  align-items: ${({ $deMim }) => ($deMim ? "flex-end" : "flex-start")};
  margin-bottom: ${({ $fimDoBloco }) => ($fimDoBloco ? "10px" : "2px")};
  min-width: 0;
`;

const ponta = css`
  &::before {
    content: "";
    position: absolute;
    top: 0;
    width: 8px;
    height: 12px;
    background: inherit;
    ${({ $deMim }) =>
      $deMim
        ? "right: -7px; clip-path: polygon(0 0, 100% 0, 0 100%);"
        : "left: -7px; clip-path: polygon(0 0, 100% 0, 100% 100%);"}
  }
`;

export const Bolha = styled.div`
  position: relative;
  max-width: min(85%, 620px);
  min-width: 72px;
  padding: 6px 9px 7px;
  border-radius: 9px;
  background: ${({ $deMim }) => ($deMim ? "var(--cp-balao-meu, var(--accent-soft))" : "var(--cp-balao, var(--surface))")};
  box-shadow: var(--shadow-1);
  color: var(--ink);
  font-size: 14px;
  line-height: 1.45;
  overflow-wrap: anywhere;

  ${({ $comPonta, $deMim }) =>
    $comPonta &&
    css`
      ${$deMim ? "border-top-right-radius: 0;" : "border-top-left-radius: 0;"}
      ${ponta}
    `}
`;

const Autor = styled.div`
  margin-bottom: 2px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--ink-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Mensagem citada (respondida), dentro do balão e acima da caixa de escrever. */
export const Citacao = styled.div`
  display: grid;
  gap: 1px;
  min-width: 0;
  margin-bottom: 5px;
  padding: 6px 9px;
  border-radius: 7px;
  background: var(--cp-midia, rgba(11, 15, 26, 0.05));
  font-size: 12.5px;
  line-height: 1.4;

  b {
    font-weight: 600;
    color: var(--ink-2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    color: var(--ink-3);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

/* O balão e a ação ao lado dele, na mesma linha. */
const BalaoComAcao = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-direction: ${({ $deMim }) => ($deMim ? "row-reverse" : "row")};
  max-width: min(calc(85% + 34px), 654px);
  min-width: 0;

  /* O balão ocupa o que sobra ao lado da ação. */
  > div:first-child {
    max-width: 100%;
  }
`;

/* Aparece ao passar o mouse ou com o foco; no toque fica sempre, bem leve. */
const BotaoResponder = styled.button`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-3);
  cursor: pointer;
  opacity: 0;
  transition: opacity 120ms ease-out, background-color 120ms ease-out;

  ${BalaoComAcao}:hover > &,
  &:focus-visible {
    opacity: 1;
  }

  &:hover {
    background: var(--cp-balao, var(--surface));
    color: var(--ink);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 0;
  }

  @media (hover: none) {
    opacity: 0.45;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/* A hora flutua no fim do texto: cabe na última linha ou desce, como no WhatsApp. */
const Corpo = styled.div`
  display: flow-root;
`;

export const Texto = styled.span`
  white-space: pre-wrap;
`;

const TextoAusente = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-style: italic;
  color: var(--ink-3);
`;

const Meta = styled.span`
  float: right;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 7px 0 -4px 12px;
  font-size: 11px;
  line-height: 1;
  color: var(--ink-3);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;

/* Cor de estado: a pessoa apagou no WhatsApp (o texto ficou guardado aqui). */
const SeloApagada = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 600;
  color: var(--danger-text);
`;

const MarcaProjetos = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-weight: 600;
  color: ${({ $ativa }) => ($ativa ? "var(--warning-text)" : "var(--ink-3)")};

  &:is(button) {
    cursor: pointer;
    border-radius: 4px;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid var(--accent);
      outline-offset: 2px;
    }
  }
`;

export const CaixaDaMidia = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: min(220px, 100%);
  margin-bottom: 4px;
  padding: 8px;
  border-radius: 7px;
  background: var(--cp-midia, rgba(11, 15, 26, 0.05));

  svg {
    flex: 0 0 auto;
  }

  > span:first-child {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: var(--cp-balao, var(--surface));
    color: var(--ink-2);
  }

  > div {
    display: grid;
    gap: 1px;
    min-width: 0;
  }

  b {
    font-size: 13.5px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  small {
    font-size: 12px;
    color: var(--ink-3);
  }
`;

const Acao = styled.div`
  margin-top: 4px;
`;

function CartaoDeMidia({ mensagem }) {
  if (mensagem.imagem) return <ImagemDaMensagem mensagem={mensagem} />;
  const midia = midiaDaMensagem(mensagem);
  if (!midia) return null;
  const Icone = ICONE_DA_MIDIA[midia.tipo] || FiImage;
  const detalhe = midia.tipo === "documento" ? "Documento" : midia.detalhe;
  return (
    <CaixaDaMidia
      title={midia.tipo === "imagem"
        ? "Imagem sem cópia guardada: chegou antes de 04/11/2026 ou numa conversa a classificar."
        : "O monitor guarda o tipo e o nome do arquivo, não o arquivo."}
    >
      <span aria-hidden="true"><Icone size={18} /></span>
      <div>
        <b>{midia.rotulo}</b>
        {detalhe && detalhe !== midia.rotulo ? <small>{detalhe}</small> : null}
      </div>
    </CaixaDaMidia>
  );
}

function ConteudoDoBalao({ mensagem }) {
  // A apagada guarda o texto (04/11/2026): só a de antes, que perdeu o texto, mostra o aviso.
  if (mensagem.apagada && !String(mensagem.texto || "") && !midiaDaMensagem(mensagem)) {
    return (
      <TextoAusente>
        <FiSlash size={13} aria-hidden="true" /> Mensagem apagada
      </TextoAusente>
    );
  }
  const texto = String(mensagem.texto || "");
  if (texto) return <Texto>{texto}</Texto>;
  if (midiaDaMensagem(mensagem)) return null;
  return <TextoAusente>{mensagem.texto_guardado === false ? "Texto não guardado" : "Mensagem sem texto"}</TextoAusente>;
}

/** A mensagem que esta responde, se estiver entre as carregadas. */
export function MensagemCitada({ mensagem }) {
  if (!mensagem) return null;
  return (
    <Citacao>
      <b>{autorDaMensagem(mensagem)}</b>
      <span>{trechoDaMensagem(mensagem)}</span>
    </Citacao>
  );
}

const PROPS_DO_SELO = Object.freeze({
  role: "note",
  "aria-label": "Mensagem para a equipe de projetos",
  title: "Mensagem para a equipe de projetos",
});

function propsDoSeloComBotao(mensagem, aoTirarSelo) {
  return {
    as: "button",
    type: "button",
    onClick: () => aoTirarSelo(mensagem),
    "aria-label": "Mensagem marcada para Projetos. Não é de Projetos? Tirar o selo",
    title: "Não é de Projetos? Clique para tirar o selo",
  };
}

/**
 * Um balão. `mostrarAutor`: nome no topo (o número fica na dica);
 * `comPonta`: primeiro balão do bloco; `citada`: a mensagem respondida;
 * `marcaProjetos`: "ativa", "antiga" ou null (ver `marcaDeProjetos`);
 * `aoTirarSelo`: liga o "Não é de Projetos" no selo (quem trata a Central).
 */
export function Balao({ mensagem, mostrarAutor = false, comPonta = true, citada = null, marcaProjetos = null, aoTirarSelo }) {
  const hora = horaCurta(mensagem.enviada_em);
  return (
    <Bolha $deMim={Boolean(mensagem.de_mim)} $comPonta={comPonta}>
      {mostrarAutor ? <Autor title={mensagem.autor_numero || undefined}>{autorDaMensagem(mensagem)}</Autor> : null}
      <MensagemCitada mensagem={citada} />
      <CartaoDeMidia mensagem={mensagem} />
      <Corpo>
        <ConteudoDoBalao mensagem={mensagem} />
        <Meta>
          {marcaProjetos ? (
            <MarcaProjetos
              $ativa={marcaProjetos === "ativa"}
              {...(aoTirarSelo ? propsDoSeloComBotao(mensagem, aoTirarSelo) : PROPS_DO_SELO)}
            >
              <IconeProjetos size={11} aria-hidden="true" />
              Projetos
            </MarcaProjetos>
          ) : null}
          {mensagem.apagada ? (
            <SeloApagada title={dicaDaApagada(mensagem)}>
              <FiSlash size={11} aria-hidden="true" />
              Apagada
            </SeloApagada>
          ) : null}
          <span>
            {mensagem.editada && !mensagem.apagada ? "editada · " : ""}
            <time dateTime={mensagem.enviada_em || undefined}>{hora}</time>
          </span>
        </Meta>
      </Corpo>
    </Bolha>
  );
}

/** Dá para citar: veio o id externo e a mensagem não foi apagada. */
function podeCitar(mensagem) {
  return Boolean(mensagem.id_externo) && !mensagem.apagada;
}

/**
 * Lista de mensagens com dias e blocos por autor.
 * - `mostrarAutores`: nome no início de cada bloco, a equipe inclusive
 *   (grupos e listas que misturam conversas).
 * - `acaoDaMensagem(m)`: algo para pôr logo abaixo do balão (ex.: criar
 *   pendência na ficha).
 * - `aoResponder(m)`: liga a ação "Responder" ao lado de cada balão.
 * - `alertaProjetos`: o alerta aberto da conversa; dá cor às marcas dele.
 * - `aoTirarSelo(m)`: o selo Projetos vira botão "Não é de Projetos".
 */
export function FluxoDeMensagens({
  mensagens,
  mostrarAutores = false,
  acaoDaMensagem,
  aoResponder,
  alertaProjetos = null,
  aoTirarSelo,
  agora,
  children,
}) {
  const dias = useMemo(() => agruparMensagens(mensagens, agora), [mensagens, agora]);
  const porIdExterno = useMemo(() => {
    const mapa = new Map();
    (mensagens || []).forEach((m) => {
      if (m.id_externo) mapa.set(m.id_externo, m);
    });
    return mapa;
  }, [mensagens]);
  return (
    <Lista aria-label="Mensagens">
      {dias.map((dia) => (
        <Dia key={dia.chave}>
          <RotuloDoDia>{dia.rotulo}</RotuloDoDia>
          <Lista>
            {dia.itens.map(({ mensagem, inicioDoBloco, fimDoBloco }) => {
              const acao = acaoDaMensagem ? acaoDaMensagem(mensagem) : null;
              return (
                <LinhaDoBalao key={mensagem.id} $deMim={Boolean(mensagem.de_mim)} $fimDoBloco={fimDoBloco}>
                  <BalaoComAcao $deMim={Boolean(mensagem.de_mim)}>
                    <Balao
                      mensagem={mensagem}
                      comPonta={inicioDoBloco}
                      mostrarAutor={mostrarAutores && inicioDoBloco}
                      citada={mensagem.citada_id ? porIdExterno.get(mensagem.citada_id) : null}
                      marcaProjetos={marcaDeProjetos(mensagem, alertaProjetos)}
                      aoTirarSelo={aoTirarSelo}
                    />
                    {aoResponder && podeCitar(mensagem) ? (
                      <BotaoResponder
                        type="button"
                        aria-label={`Responder a ${autorDaMensagem(mensagem)}`}
                        title="Responder citando esta mensagem"
                        onClick={() => aoResponder(mensagem)}
                      >
                        <FiCornerUpLeft size={16} aria-hidden="true" />
                      </BotaoResponder>
                    ) : null}
                  </BalaoComAcao>
                  {acao ? <Acao>{acao}</Acao> : null}
                </LinhaDoBalao>
              );
            })}
          </Lista>
        </Dia>
      ))}
      {children}
    </Lista>
  );
}

/** Mensagens numa caixa própria (ficha, origem), abrindo na mais recente como no WhatsApp. */
export function MensagensNaCaixa({ mensagens, ...props }) {
  const caixa = useRef(null);
  useLayoutEffect(() => {
    if (caixa.current) caixa.current.scrollTop = caixa.current.scrollHeight;
  }, [mensagens]);
  return (
    <CaixaDeMensagens ref={caixa}>
      <FluxoDeMensagens mensagens={mensagens} {...props} />
    </CaixaDeMensagens>
  );
}
